import { CommonModule } from '@angular/common';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
    SiApplicationHeaderComponent,
    SiHeaderActionsDirective,
    SiHeaderBrandDirective,
    SiHeaderLogoDirective
} from '@siemens/element-ng/application-header';
import { SiHeaderDropdownComponent, SiHeaderDropdownTriggerDirective } from '@siemens/element-ng/header-dropdown';
import { SiAvatarComponent } from '@siemens/element-ng/avatar';
import { SiIconComponent } from '@siemens/element-ng/icon';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import {
    HeaderActionsComponent,
    HeaderComponent,
    HeaderProfileComponent,
    HeaderProfileSelectorComponent
} from '@app-root-components';
import { RootServicesModule } from '@app-root-services';
import { SharedModule } from '@app-shared';
import { JwtModule } from '@auth0/angular-jwt';
import { TranslateLoader, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import {
    SiTranslateNgxTModule,
    provideMissingTranslationHandlerForElement
} from '@siemens/element-translate-ng/ngx-translate';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FooterComponent } from './components/footer/footer.component';
import { EnergyRetirementNoticeComponent } from './components/energy-retirement-notice/energy-retirement-notice.component';
import { MsalService } from '@azure/msal-angular';
import { environment } from '@app-environment';
import { TranslationData } from './interfaces/translation.interface';

export function getToken() {
    return localStorage.getItem('idToken') || '';
}

@Injectable()
export class CustomLoader implements TranslateLoader {
    constructor(
        private http: HttpClient,
        private msalService: MsalService
    ) {}

    getTranslation(lang: string) {
        const local$ = this.http
            .get<TranslationData>(`assets/i18n/${lang}.json`)
            .pipe(catchError(() => of({} as TranslationData)));
        // Only call the protected translations API when an authenticated account exists; otherwise the
        // MsalInterceptor fires acquireTokenRedirect and races the MsalGuard's loginRedirect, aborting
        // the auth navigation (ERR_ABORTED / request stuck pending, no token).
        if (this.msalService.instance.getAllAccounts().length === 0) {
            return local$;
        }
        const server$ = this.http
            .get<TranslationData>(`${environment.gatewayAPI}/translations/${lang}`)
            .pipe(catchError(() => of({} as TranslationData)));
        // Server translations take precedence over local fallback translations.
        return forkJoin([local$, server$]).pipe(map(([local, server]) => ({ ...local, ...server })));
    }
}

@NgModule({
    declarations: [
        HeaderComponent,
        HeaderProfileComponent,
        HeaderActionsComponent,
        HeaderProfileSelectorComponent,
        EnergyRetirementNoticeComponent
    ],
    imports: [
        CommonModule,
        RouterModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: getToken
            }
        }),
        RootServicesModule,
        HttpClientModule,
        SharedModule,
        SiApplicationHeaderComponent,
        SiHeaderActionsDirective,
        SiHeaderBrandDirective,
        SiHeaderLogoDirective,
        SiHeaderDropdownComponent,
        SiHeaderDropdownTriggerDirective,
        SiAvatarComponent,
        SiIconComponent,
        SiTooltipDirective,
        FooterComponent,
        SiTranslateNgxTModule
    ],
    providers: [
        ...provideTranslateService({
            loader: provideTranslateLoader(CustomLoader),
            missingTranslationHandler: provideMissingTranslationHandlerForElement()
        })
    ],
    exports: [
        HeaderComponent,
        HeaderProfileComponent,
        HeaderActionsComponent,
        HeaderProfileSelectorComponent,
        FooterComponent
    ]
})
export class RootModule {}
